/**
 * Ganancias.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "Cuanto gane, y de donde salio."
 *
 * Es la otra mitad de Caja. Caja contesta por plata que existe hoy —cuanta hay,
 * donde esta, que se movio—; aca se contesta por periodos cerrados, que es una
 * pregunta de otra frecuencia: la caja se mira todo el dia, esto una vez por
 * semana. Estaban las dos en la misma pantalla, una abajo de la otra, y por eso
 * ninguna se leia.
 *
 * LAS DOS GANANCIAS NO SE SUMAN EN UN NUMERO
 * ------------------------------------------
 * El margen operativo es lo que deja vender y se repite. El resultado
 * financiero sale de la brecha del dolar y no: puede desaparecer de un dia para
 * el otro sin que nadie haga nada distinto. Sumarlos arriba de todo haria que
 * el negocio parezca mejor de lo que es, asi que el total vive al pie de la
 * tabla y no en la cabecera.
 *
 * TRES SOLAPAS Y NO UN SCROLL
 * ---------------------------
 * A esta pantalla se entra con UNA pregunta: cuanto gane, cuanto me cobro la
 * tarjeta, o si me convino ese cambio. Son tres preguntas que no se hacen
 * juntas, asi que apilarlas seria repetir el problema que esta pantalla vino a
 * resolver.
 */
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { TrendingUp } from "lucide-react";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Segmented } from "@/components/ui/segmented";
import { SelectorPeriodo } from "@/components/ui/selector-periodo";
import { CambiosDivisa } from "@/components/ganancias/CambiosDivisa";
import { CostosEstimados } from "@/components/ganancias/CostosEstimados";
import { GastosPorCategoria } from "@/components/ganancias/GastosPorCategoria";
import { KpisGanancia, PorDia } from "@/components/ganancias/PorDia";
import { Tarjetas } from "@/components/ganancias/Tarjetas";
import {
  useCambiosDivisa,
  useCostosEstimados,
  useGastosPorCategoria,
  useResultadoNegocio,
  useTarjetas,
} from "@/lib/queries";
import { esteMes, rangoDelPeriodo, type Periodo } from "@/lib/periodo";

const VISTAS = [
  { valor: "dia", etiqueta: "Por día" },
  { valor: "cambios", etiqueta: "Cambios de divisa" },
  { valor: "tarjetas", etiqueta: "Tarjetas" },
  { valor: "gastos", etiqueta: "Gastos por categoría" },
] as const;

type Vista = (typeof VISTAS)[number]["valor"];

/**
 * La solapa puede venir en la URL: «Tarjetas por acreditar», en Caja, lleva a
 * `?vista=tarjetas`. Antes la pantalla abria siempre en «Por día» y el que
 * tocaba la franja no encontraba lo que habia tocado.
 */
export const Route = createFileRoute("/ganancias")({
  validateSearch: (busqueda: Record<string, unknown>): { vista?: Vista } => {
    const vista = VISTAS.find((v) => v.valor === busqueda.vista)?.valor;
    return vista ? { vista } : {};
  },
  component: Ganancias,
});

function Ganancias(): JSX.Element {
  const [periodo, setPeriodo] = useState<Periodo>(() => esteMes());
  // La solapa ES la URL, sin copia en un estado: con la copia, tocar una
  // solapa no cambiaba la URL, y volver por el link de «Tarjetas por
  // acreditar» no hacia nada si la URL ya decia `?vista=tarjetas`.
  const { vista = "dia" } = Route.useSearch();
  const navegar = Route.useNavigate();
  const elegirVista = (v: Vista): void => {
    void navegar({ search: v === "dia" ? {} : { vista: v } });
  };

  const rango = useMemo(() => rangoDelPeriodo(periodo), [periodo]);

  // Las tres consultas cuelgan del mismo periodo. Las que no se ven igual se
  // piden: son tres `sum` sobre indice y traen la solapa ya cargada cuando se
  // la toca.
  const { data: negocio } = useResultadoNegocio(rango);
  const { data: cambios } = useCambiosDivisa(rango);
  const { data: tarjetas } = useTarjetas(rango);
  // Lo vendido sin stock: su costo es estimado hasta que entra la compra. Lo
  // vendido con costo 0 viene del periodo: sin el rango, una venta de
  // septiembre avisaba que el margen de agosto estaba inflado.
  const { data: estimados } = useCostosEstimados(rango);
  const { data: gastos } = useGastosPorCategoria(rango);

  const filasNegocio = negocio?.filas ?? [];

  return (
    <Pagina alto="fijo">
      <PageHeader
        titulo="Ganancias"
        icono={TrendingUp}
        acciones={<SelectorPeriodo valor={periodo} onChange={setPeriodo} />}
      />

      <KpisGanancia filas={filasNegocio} />

      <CostosEstimados filas={estimados?.filas ?? []} />

      <Segmented
        etiqueta="Qué mirar"
        apariencia="chips"
        tamano="sm"
        opciones={VISTAS.map((v) => ({ valor: v.valor, etiqueta: v.etiqueta }))}
        valor={vista}
        onChange={(v) => elegirVista(v as Vista)}
      />

      {vista === "dia" ? <PorDia filas={filasNegocio} /> : null}
      {vista === "cambios" ? <CambiosDivisa filas={cambios?.filas ?? []} /> : null}
      {vista === "tarjetas" ? (
        <Tarjetas filas={tarjetas?.filas ?? []} totales={tarjetas?.totales} />
      ) : null}
      {vista === "gastos" ? <GastosPorCategoria filas={gastos?.filas ?? []} /> : null}
    </Pagina>
  );
}
